import { ChangeEventHandler } from 'react';

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Divider,
    TextField,
} from '@mui/material';

import { SimpleLinkProps } from 'components/atoms/Link/types';
import { Typography } from 'components/atoms/Typography';
import { SimpleLinkConfig } from 'types/builder/componentConfig';

import { ImageSelector } from '../../editorControls/ImageSelector';
import { InternalPageSelector } from '../../editorControls/InternalPageSelector';

type LinkControlProps = {
    node?: SimpleLinkConfig;
    onChange: (node?: SimpleLinkConfig) => void;
};

type LinkType = {
    value: SimpleLinkProps['type'];
    title: string;
};

const types: LinkType[] = [
    {
        value: 'internal',
        title: 'Ссылка на внутреннюю страницу',
    },
    {
        value: 'external',
        title: 'Ссылка на внешний ресурс',
    },
    {
        value: 'image',
        title: 'Ссылка на картинку',
    },
];

type Variant = SimpleLinkProps['variant'];

const variants: {
    value: Variant;
    title: string;
}[] = [
    {
        value: 'display.medium',
        title: 'Заголовок 1',
    },
    {
        value: 'title.large',
        title: 'Заголовок 2',
    },
    {
        value: 'title.medium',
        title: 'Заголовок 3',
    },
    {
        value: 'title.small',
        title: 'Параграф (жирный)',
    },
    {
        value: 'body.medium',
        title: 'Параграф',
    },
];

export const LinkControl = ({
    onChange,
    node,
}: LinkControlProps): JSX.Element => {
    const {
        href = '',
        hrefUuid = '',
        type = 'internal',
        variant = 'body.medium',
        fullWidth = false,
        showArrow = false,
        imageUuid,
        children,
    } = node?.props || {};

    const onLinkTypeChange = (value: SimpleLinkProps['type']): void => {
        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                type: value,
                href: '',
                imageUuid: undefined,
                hrefUuid: undefined,
            },
        });
    };

    const onArrowChange = (value: boolean): void => {
        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                showArrow: value,
            },
        });
    };
    const onFullWidthChange = (value: boolean): void => {
        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                fullWidth: value,
            },
        });
    };

    const onLinkTextChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.target;

        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                children: value,
            },
        });
    };

    const onVariantChange = (value: SimpleLinkProps['variant']): void => {
        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                variant: value,
            },
        });
    };

    const onLinkHrefChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.target;

        onChange({
            component: 'simpleLink',
            props: {
                ...node?.props,
                hrefUuid: undefined,
                imageUuid: undefined,
                href: value,
            },
        });
    };

    return (
        <Stack spacing={2}>
            <FormControl variant="outlined" size="small">
                <InputLabel id="base-route-label">Вариант</InputLabel>
                <Select
                    labelId="base-route-label"
                    label="Вариант"
                    value={variant || ''}
                    renderValue={(v) => (
                        <span>
                            {variants.find((el) => el.value === v)?.title}
                        </span>
                    )}
                >
                    {variants.map((el, i) => (
                        <MenuItem
                            key={`menu_item_${i}`}
                            value={el.value}
                            onClick={() => onVariantChange(el.value)}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Typography variant={el.value}>
                                    {el.title}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl variant="outlined" size="small">
                <InputLabel id="base-route-label">Тип ссылки</InputLabel>
                <Select
                    labelId="base-route-label"
                    label="Тип ссылки"
                    value={type || ''}
                >
                    {types.map((el, i) => (
                        <MenuItem
                            key={`menu_item_${i}`}
                            value={el.value}
                            onClick={() => onLinkTypeChange(el.value)}
                        >
                            {el.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {type === 'internal' ? (
                <InternalPageSelector
                    value={hrefUuid}
                    onChange={(v) =>
                        onChange({
                            component: 'simpleLink',
                            props: {
                                ...node?.props,
                                href: undefined,
                                imageUuid: undefined,
                                hrefUuid: v,
                            },
                        })
                    }
                />
            ) : undefined}

            {type === 'external' ? (
                <FormControl variant="outlined">
                    <TextField
                        label="Внешний ресурс"
                        size="small"
                        fullWidth
                        value={href || ''}
                        onChange={onLinkHrefChange}
                    />
                </FormControl>
            ) : undefined}
            {type === 'image' ? (
                <ImageSelector
                    defaultSelectedUuid={imageUuid}
                    onSelect={(img) =>
                        onChange({
                            component: 'simpleLink',
                            props: {
                                ...node?.props,
                                hrefUuid: undefined,
                                href: undefined,
                                imageUuid: img.uuid,
                            },
                        })
                    }
                />
            ) : undefined}

            <TextField
                label="Текст ссылки"
                size="small"
                variant="outlined"
                fullWidth
                multiline
                value={children || ''}
                onChange={onLinkTextChange}
                autoFocus
            />
            <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography>Показывать стрелку</Typography>
                    <Switch
                        name="showTopAppBar"
                        checked={showArrow}
                        onChange={(e, checked) => onArrowChange(checked)}
                    />
                </Stack>
                <Divider />
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography>По всей ширине</Typography>
                    <Switch
                        name="showTopAppBar"
                        checked={fullWidth}
                        onChange={(e, checked) => onFullWidthChange(checked)}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};
